<script setup lang="ts">
import { useAuth } from '@clerk/vue'
import { ref, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { UserButton } from '@clerk/vue'
import { useImpersonation } from '@/composables/useImpersonation'

const router = useRouter()
const route = useRoute()
const { isLoaded, isSignedIn } = useAuth()
const { impersonating, impersonatedOrgName, exitImpersonation, checkStatus } = useImpersonation()

const appVersion = __APP_VERSION__

const sidebarOpen = ref(false)

// Close sidebar on route change (mobile)
watch(() => route.path, () => {
  sidebarOpen.value = false
})

// Redirect to sign-in when not authenticated
watch([isLoaded, isSignedIn], ([loaded, signedIn]) => {
  if (loaded && !signedIn) {
    router.replace('/sign-in')
  }
}, { immediate: true })

// Check impersonation status on mount
onMounted(() => {
  checkStatus()
})

interface NavItem {
  label: string
  icon: string
  to: string
}

const navItems: NavItem[] = [
  { label: 'Overview', icon: 'dashboard', to: '/admin' },
  { label: 'Customers', icon: 'people', to: '/admin/customers' },
  { label: 'Subscriptions', icon: 'receipt', to: '/admin/subscriptions' },
  { label: 'System Health', icon: 'monitor', to: '/admin/system-health' },
]

function isActive(to: string): boolean {
  if (to === '/admin') return route.path === '/admin'
  return route.path.startsWith(to)
}

function navigate(to: string) {
  router.push(to)
}

const iconMap: Record<string, string> = {
  dashboard: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z',
  people: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  receipt: 'M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z',
  monitor: 'M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z',
}
</script>

<template>
  <!-- Loading state while Clerk initializes -->
  <div v-if="!isLoaded" class="min-h-screen bg-surface flex items-center justify-center">
    <div class="animate-sync-pulse text-text-muted font-label text-label-md uppercase tracking-[0.05em]">
      Loading...
    </div>
  </div>

  <!-- Not signed in -->
  <div v-else-if="!isSignedIn" class="min-h-screen bg-surface flex items-center justify-center">
    <div class="text-text-muted font-label text-label-md uppercase tracking-[0.05em]">
      Redirecting to sign in...
    </div>
  </div>

  <!-- Signed in: admin layout -->
  <div v-else class="min-h-screen bg-surface">
    <!-- Mobile header bar -->
    <div class="lg:hidden fixed top-0 left-0 right-0 z-40 bg-surface-lowest px-md py-sm flex items-center justify-between">
      <div class="flex items-center gap-sm">
        <img src="/favicon.svg" alt="FlagDrop" class="w-6 h-6" />
        <span class="font-display text-body-md font-bold text-text-primary">FlagDrop</span>
        <span class="text-label-sm font-label uppercase tracking-[0.05em] text-warning ml-xs">Admin</span>
      </div>
      <button
        class="btn-ghost !p-xs"
        @click="sidebarOpen = !sidebarOpen"
        aria-label="Toggle menu"
      >
        <svg v-if="!sidebarOpen" class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
        <svg v-else class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Backdrop overlay (mobile) -->
    <Transition
      enter-active-class="transition-opacity duration-short ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-short ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="sidebarOpen"
        class="lg:hidden fixed inset-0 z-30 bg-black/50"
        @click="sidebarOpen = false"
      />
    </Transition>

    <!-- Admin Sidebar -->
    <div
      :class="[
        'fixed left-0 top-0 bottom-0 w-[220px] z-40 transition-transform duration-medium ease-in-out',
        'lg:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
    >
      <aside class="w-[220px] h-full bg-surface-lowest flex flex-col">
        <!-- Logo + Admin badge -->
        <div class="px-md py-lg flex items-center gap-sm">
          <img src="/favicon.svg" alt="FlagDrop" class="w-7 h-7" />
          <span class="font-display text-heading font-bold text-text-primary">FlagDrop</span>
          <span class="text-label-sm font-label uppercase tracking-[0.05em] text-warning ml-auto">Admin</span>
        </div>

        <!-- Back to Dashboard link -->
        <div class="px-sm mb-sm">
          <button
            class="w-full flex items-center gap-sm px-sm py-[10px] rounded-md text-body-sm font-body text-text-muted hover:text-text-primary hover:bg-surface-container transition-colors duration-short"
            @click="navigate('/dashboard')"
          >
            <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            <span>Back to Dashboard</span>
          </button>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-sm overflow-y-auto">
          <div class="mb-xs">
            <span class="section-label px-sm">Administration</span>
          </div>
          <button
            v-for="item in navItems"
            :key="item.to"
            class="w-full flex items-center gap-sm px-sm py-[10px] rounded-md text-body-sm font-body transition-colors duration-short"
            :class="isActive(item.to)
              ? 'bg-surface-container-high text-text-primary'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-container'"
            @click="navigate(item.to)"
          >
            <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path :d="iconMap[item.icon]" />
            </svg>
            <span>{{ item.label }}</span>
          </button>
        </nav>

        <!-- User section -->
        <div class="px-md py-md border-t border-outline/15">
          <UserButton />
          <p class="font-mono text-[10px] text-text-muted/50 mt-sm">v{{ appVersion }}</p>
        </div>
      </aside>
    </div>

    <!-- Impersonation banner -->
    <div
      v-if="impersonating"
      class="fixed top-0 left-0 right-0 z-50 bg-warning/90 text-surface-lowest px-md py-sm flex items-center justify-center gap-md text-body-sm font-label"
    >
      <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
      </svg>
      <span>
        Viewing as <strong>{{ impersonatedOrgName }}</strong>
        <span class="hidden sm:inline"> &mdash; Read-only mode</span>
      </span>
      <button
        class="ml-sm px-sm py-[2px] rounded-md bg-surface-lowest/20 hover:bg-surface-lowest/40 text-surface-lowest font-label text-label-sm uppercase tracking-[0.05em] transition-colors duration-short"
        @click="exitImpersonation"
      >
        Exit Impersonation
      </button>
    </div>

    <!-- Main content -->
    <main
      class="min-h-screen lg:ml-[220px] pt-[52px] lg:pt-0"
      :class="{ 'pt-[88px] lg:pt-[36px]': impersonating }"
    >
      <div class="p-md sm:p-lg lg:p-xl">
        <slot />
      </div>
    </main>
  </div>
</template>
